import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'viq-theme';

export interface ThemeStore {
  theme: Theme;
  /** True while the dark-first default is active because no explicit choice
   * has been stored. */
  isSystem: boolean;
  setTheme: (next: Theme) => void;
  toggleTheme: () => void;
}

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    // Storage may be unavailable (private mode, disabled cookies); dark is
    // still the in-memory default for this session.
    return null;
  }
}

function defaultTheme(): Theme {
  // The workspace is intentionally dark-first. A user choice always wins.
  return 'dark';
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * Chrome dark-mode state (never touches the document/.page-canvas, which
 * stays light regardless of theme - see styles.css). Mirrors the inline
 * bootstrap script in index.html, which sets the attribute before first
 * paint from the same localStorage key: an explicit choice wins and is
 * persisted; with no choice, both layers use dark before the first paint.
 */
export function useTheme(): ThemeStore {
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? defaultTheme());
  const [isSystem, setIsSystem] = useState<boolean>(() => readStored() === null);

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The in-memory theme still applies for this session even if it can't
      // be persisted.
    }

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (typeof document.startViewTransition === 'function' && !reduceMotion) {
      // Let the browser snapshot the current screen, then flip state (and the
      // data-theme attribute) synchronously so the new screen - icon swap
      // included - is captured in the second snapshot. The browser
      // cross-fades the two bitmaps on the GPU.
      const transition = document.startViewTransition(() => {
        flushSync(() => {
          setIsSystem(false);
          setThemeState(next);
        });
        applyTheme(next);
      });
      // The browser skips the transition when the document is hidden or a
      // newer toggle interrupts this one; `ready` then rejects. The theme
      // has already been applied by the callback, so a skipped fade is not
      // an error.
      transition.ready.catch(() => {});
    } else {
      // No View Transitions support, or the user asked for less motion:
      // flip instantly.
      setIsSystem(false);
      setThemeState(next);
      applyTheme(next);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, isSystem, setTheme, toggleTheme };
}
